import { useLayoutEffect, useRef, useState, type ComponentType, type ReactNode } from 'react';
import { AlertCircleIcon, AlertTriangleIcon, InboxIcon, RefreshCwIcon } from 'lucide-react';
import { NavLink } from 'react-router';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { withCleanup } from '@/lib/with-cleanup';

import type { RetryFocus } from './retry-focus';
import { SuccessMark } from './success-mark';

type IconComponent = ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;

/**
 * Re-runs a failed load. A load that returns its promise gets a retry button
 * that shows progress and ignores repeat presses until it settles; the load
 * reports its own failure through the error state that rendered the button.
 * A load that only triggers a refetch shows its progress where it loads.
 */
type RetryHandler = (() => Promise<void>) | (() => void);

interface RetryButtonProps {
  readonly onRetry: RetryHandler;
  readonly label: string;
  readonly size: 'default' | 'sm';
  /** Identifies the error surface across remounts within its owner. */
  readonly surface: string;
  readonly retryFocus: RetryFocus | undefined;
}

function RetryButton({ onRetry, label, size, surface, retryFocus }: RetryButtonProps) {
  const [retrying, setRetrying] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Claimed before paint, so focus cannot lag behind the remounted error.
  useLayoutEffect(() => {
    if (retryFocus?.claim(surface) === true) buttonRef.current?.focus();
  }, [retryFocus, surface]);

  async function retry(): Promise<void> {
    if (retrying) return;
    retryFocus?.arm(surface);
    setRetrying(true);
    await withCleanup(async () => {
      await onRetry();
    }, () => {
      retryFocus?.settle(surface);
      setRetrying(false);
    });
  }

  // aria-disabled rather than disabled keeps keyboard focus on the button
  // while the retry runs.
  return (
    <Button
      aria-busy={retrying}
      aria-disabled={retrying}
      onClick={() => {
        void retry();
      }}
      ref={buttonRef}
      size={size}
      type="button"
      variant="outline"
    >
      {retrying ? (
        <Spinner aria-hidden="true" data-icon="inline-start" essential />
      ) : (
        <RefreshCwIcon data-icon="inline-start" />
      )}
      {label}
    </Button>
  );
}

interface InlineErrorStateProps {
  readonly title: string;
  readonly message: string;
  readonly onRetry: RetryHandler;
  /** Names what is retried when a page shows several of these, e.g. "Retry user load". */
  readonly retryLabel?: string;
  /**
   * The owning component's `useRetryFocus()`, so a retry that fails again
   * after the page showed its loading state in between gets focus back. Needs
   * an `onRetry` that returns the load's promise; a retry that only triggers
   * a refetch cannot tell its outcome apart.
   */
  readonly retryFocus?: RetryFocus;
}

/**
 * A section of a page failed to load: says what failed and offers a retry in
 * place, leaving the rest of the page usable. A page that failed as a whole
 * uses `PageErrorState` instead.
 */
export function InlineErrorState({
  title,
  message,
  onRetry,
  retryLabel = 'Retry',
  retryFocus,
}: InlineErrorStateProps) {
  return (
    <Alert variant="destructive">
      <AlertTriangleIcon />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription className="flex flex-col items-start gap-3">
        <span>{message}</span>
        <RetryButton
          label={retryLabel}
          onRetry={onRetry}
          retryFocus={retryFocus}
          size="sm"
          surface={`${title}\n${retryLabel}`}
        />
      </AlertDescription>
    </Alert>
  );
}

interface PageErrorStateProps {
  readonly title?: string;
  readonly message: string;
  readonly onRetry?: RetryHandler;
  readonly backHref?: string;
  readonly backLabel?: string;
  readonly actions?: ReactNode;
  readonly className?: string;
}

export function PageErrorState({
  title = 'Something went wrong',
  message,
  onRetry,
  backHref,
  backLabel,
  actions,
  className,
}: PageErrorStateProps) {
  return (
    <Card className={cn('border-destructive/30 bg-destructive/5', className)}>
      <CardHeader className="items-center text-center">
        <div className="flex size-12 items-center justify-center rounded-full bg-destructive/10 ring-4 ring-destructive/5">
          <AlertCircleIcon className="size-6 text-destructive" aria-hidden="true" />
        </div>
        <div className="flex max-w-xl flex-col gap-2">
          <CardTitle className="font-heading text-2xl font-bold tracking-tight">{title}</CardTitle>
          <CardDescription className="text-sm font-light leading-relaxed text-muted-foreground">
            {message}
          </CardDescription>
        </div>
      </CardHeader>
      {onRetry || backHref || actions ? (
        <CardContent className="flex flex-wrap justify-center gap-3">
          {onRetry ? (
            <RetryButton
              label="Retry"
              onRetry={onRetry}
              retryFocus={undefined}
              size="default"
              surface={`${title}\nRetry`}
            />
          ) : null}
          {backHref ? (
            <Button asChild variant="ghost">
              <NavLink to={backHref}>{backLabel ?? 'Back'}</NavLink>
            </Button>
          ) : null}
          {actions}
        </CardContent>
      ) : null}
    </Card>
  );
}

interface PageEmptyStateProps {
  readonly icon?: IconComponent;
  readonly title: string;
  readonly description: string;
  readonly actions?: ReactNode;
  readonly className?: string;
}

export function PageEmptyState({
  icon: Icon = InboxIcon,
  title,
  description,
  actions,
  className,
}: PageEmptyStateProps) {
  return (
    <Card className={cn('border-dashed border-border/70 bg-secondary/20', className)}>
      <div className="flex flex-col items-center gap-4 px-4 pt-4 text-center">
        <div className="flex size-14 items-center justify-center rounded-full bg-primary/10 ring-4 ring-primary/5">
          <Icon className="size-7 text-primary" aria-hidden={true} />
        </div>
        <div className="flex max-w-xl flex-col items-center gap-2 text-center">
          <CardTitle className="text-center font-heading text-2xl font-bold tracking-tight">
            {title}
          </CardTitle>
          <CardDescription className="text-center text-sm font-light leading-relaxed">
            {description}
          </CardDescription>
        </div>
      </div>
      {actions ? (
        <CardContent className="flex flex-wrap justify-center gap-3">{actions}</CardContent>
      ) : null}
    </Card>
  );
}

interface PageLoadingStateProps {
  readonly variant?: 'full' | 'compact';
  readonly label?: string;
  readonly className?: string;
}

export function PageLoadingState({
  variant = 'full',
  label,
  className,
}: PageLoadingStateProps) {
  if (variant === 'compact') {
    return (
      <div className={cn('flex flex-1 items-center justify-center px-6 py-16', className)}>
        <div className="flex flex-col items-center gap-3 text-center" role="status" aria-live="polite">
          <Spinner className="size-5" aria-hidden="true" essential={!label} />
          {label ? (
            <p className="text-sm font-light text-muted-foreground">{label}</p>
          ) : (
            <span className="sr-only">Loading…</span>
          )}
        </div>
      </div>
    );
  }
  return (
    <div aria-hidden="true" className={cn('flex flex-col gap-6', className)}>
      <div className="flex flex-col gap-3">
        <Skeleton className="h-3 w-36 rounded-full" />
        <Skeleton className="h-10 w-72 rounded-xl" />
        <Skeleton className="h-5 w-full max-w-2xl rounded-lg" />
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        {Array.from({ length: 3 }, (_, index) => (
          <Skeleton key={index} className="h-32 rounded-xl" />
        ))}
      </div>
      <Skeleton className="h-64 rounded-xl" />
      <Skeleton className="h-80 rounded-xl" />
    </div>
  );
}

interface FullPageStateProps {
  readonly title: string;
  readonly message?: string;
  /** `success` closes a rare flow the user finished, so it lands with the success mark. */
  readonly tone?: 'neutral' | 'success' | 'error';
  readonly actions?: ReactNode;
}

function FullPageStateGlyph({ tone }: { readonly tone: 'neutral' | 'success' | 'error' }): ReactNode {
  switch (tone) {
    case 'error':
      return (
        <div className="flex size-12 items-center justify-center rounded-full bg-destructive/10 ring-4 ring-destructive/5">
          <AlertCircleIcon className="size-6 text-destructive" aria-hidden="true" />
        </div>
      );
    case 'success':
      return <SuccessMark className="size-12" />;
    case 'neutral':
      return null;
  }
}

export function FullPageState({ title, message, tone = 'neutral', actions }: FullPageStateProps) {
  const landing = tone === 'success';
  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-secondary px-6 text-center">
      <FullPageStateGlyph tone={tone} />
      <div className={cn('flex max-w-xl flex-col gap-2', landing && 'ca-delight-enter ca-delight-stagger-2')}>
        <h1 className="font-heading text-2xl font-bold tracking-tight">{title}</h1>
        {message ? <p className="text-sm font-light text-muted-foreground">{message}</p> : null}
      </div>
      {actions ? (
        <div className={cn('flex flex-wrap justify-center gap-3', landing && 'ca-delight-enter ca-delight-stagger-3')}>
          {actions}
        </div>
      ) : null}
    </div>
  );
}
